'use client';

import { useState, useTransition } from 'react';
import { useToast } from '@/hooks/use-toast';
import { BadgeCheck, Loader2, Save } from 'lucide-react';
import { SOCIAL_LINK_FIELDS } from '@/lib/profile/socialLinkFields';
import { saveSocialLinks } from '@/app/(public)/profile/[userId]/social-profile-links/actions';

interface Props {
  userId: string;
  initialValues: Record<string, string | undefined>;
}

/**
 * Member UI for entering off-site profile links.
 *
 * Why this matters more than it looks: `sameAs` is how a search engine or AI engine
 * decides that the profile on zamzi.co.za and the account on Instagram / LinkedIn /
 * Facebook are the same entity. Without it those accounts are invisible to entity
 * resolution, so the platform cannot corroborate the member's identity off-site.
 *
 * In the 2026-10-03 baseline, linkedin.com was the #2 most-retrieved domain across all
 * three engines (60 citations). Getting members' real profiles linked is the cheapest
 * available entity corroboration.
 */
export default function SocialProfileLinksForm({ userId, initialValues }: Props) {
  const { toast } = useToast();
  const [isPending, startTransition] = useTransition();
  const [values, setValues] = useState<Record<string, string>>(() => {
    const seeded: Record<string, string> = {};
    for (const { field } of SOCIAL_LINK_FIELDS) {
      seeded[field] = initialValues[field] ?? '';
    }
    return seeded;
  });

  const filledCount = Object.values(values).filter((value) => value.trim().length > 0).length;

  const handleSave = () => {
    startTransition(async () => {
      try {
        const result = await saveSocialLinks({ userId, links: values });

        if (!result.success) {
          toast({
            title: 'Could not save',
            description: result.message ?? 'Please try again.',
            variant: 'destructive',
          });
          return;
        }

        if (result.rejected.length) {
          toast({
            title: 'Some links were not saved',
            description: `Check: ${result.rejected.join(', ')}. Each needs a full https:// address.`,
            variant: 'destructive',
          });
          return;
        }

        toast({
          title: 'Links saved',
          description: `${result.saved} saved. They now appear as trust signals on your public profile.`,
        });
      } catch {
        toast({
          title: 'Could not save',
          description: 'Something went wrong. Please try again.',
          variant: 'destructive',
        });
      }
    });
  };

  return (
    <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
            <BadgeCheck className="h-5 w-5 text-[#001489]" />
            Link your other profiles
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-slate-600">
            These links tell Google and AI assistants that your Instagram, LinkedIn or
            Facebook page belongs to the same business as this profile. That is what makes
            you findable when someone nearby asks an AI assistant for a recommendation —
            and it is free.
          </p>
        </div>
        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
          {filledCount} of {SOCIAL_LINK_FIELDS.length} added
        </span>
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        {SOCIAL_LINK_FIELDS.map(({ field, label, placeholder, help }) => (
          <div key={field} className="space-y-1.5">
            <label htmlFor={`social-${field}`} className="block text-sm font-medium text-slate-700">
              {label}
            </label>
            <input
              id={`social-${field}`}
              type="url"
              inputMode="url"
              placeholder={placeholder}
              value={values[field] ?? ''}
              onChange={(event) =>
                setValues((prev) => ({ ...prev, [field]: event.target.value }))
              }
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#001489] focus:ring-1 focus:ring-[#001489]"
            />
            <p className="text-xs text-slate-500">{help}</p>
          </div>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleSave}
          disabled={isPending}
          className="inline-flex items-center gap-2 rounded-lg bg-[#001489] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#001166] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {isPending ? 'Saving…' : 'Save links'}
        </button>
        <p className="text-xs text-slate-500">
          Leave a field empty to remove that link.
        </p>
      </div>
    </div>
  );
}